/**
 * PAVING (2026-10-08, docs/bim-plan/26-paving-spike.md) — the pure design resolver every
 * paving consumer reads (3D, plan, spot elevations, the palette's slope readout): the
 * flattened centerline and its stations, the width strips, the joint layout, and the
 * PROFILE (z along the path) + CROSS-SLOPE (z across it). Nothing here is stored —
 * the element keeps intent only (path, end elevations / pins, grade breaks, slopes).
 *
 * The design surface is z(s, t) = profile(s) + cross(t), s = station along the
 * centerline, t = signed offset (left of travel = +). Pins (an end following a floor or
 * the terrain) need other elements, so they resolve in bim-geometry and arrive here as
 * plain end z values (`PavingPins`).
 */
import type { ParamValue, Path2D, Pt2 } from './types'
import type { ResolvedElement } from './params'
import { flattenPathPts } from './arc'

const IN = 1 / 12

export interface PavingSpec {
  /** 'run' = a driveway / walk down its centerline; 'street' = drawn along OUR flowline */
  kind: 'run' | 'street'
  /** a street's cross section (kind 'street' only) */
  street: StreetSection | null
  /** the flattened centerline (a street: its flowline) (arcs → chords) */
  pts: Pt2[]
  /** cumulative station at each `pts` vertex (feet) */
  stations: number[]
  lengthFt: number
  widthFt: number
  thicknessFt: number
  /** 0 = no joints */
  jointSpacingFt: number
  crossSlopePct: number
  crossSlopeSide: 'left' | 'right' | 'crown'
  maxSlopePct: number
  edge: 'flush' | 'thickened' | 'curb'
  thickenedWidthFt: number
  thickenedDepthFt: number
  curbHeightFt: number
  curbWidthFt: number
  materialId: string
  /** the unpinned end elevations (project-relative feet) */
  startZ: number
  endZ: number
  /** grade breaks inside the run, ordered by station */
  breaks: { stationFt: number; zFt: number }[]
}

export interface PavingPins { startZ?: number | null; endZ?: number | null }

const numOr = (v: ParamValue | undefined, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d)

/** the end's own elevation - a terrain / slab end that can't resolve (no terrain under it,
 *  no slab picked yet) falls back to its level + offset so it never vanishes */
function endZ(el: ResolvedElement, end: 'start' | 'end'): number {
  const P = el.params
  if (P[`${end}Mode`] === 'site') return numOr(P[`${end}SiteElevationFt`], 0)
  return (el.level?.elevationFt ?? 0) + numOr(P[`${end}OffsetFt`], 0)
}

export function pavingSpec(el: ResolvedElement): PavingSpec | null {
  if (el.category !== 'paving') return null
  const path = el.params.path as Path2D | null
  if (!path?.nodes || path.nodes.length < 2) return null
  const raw = flattenPathPts(path, 0.05)
  // drop zero-length steps (a double click lands two nodes on one point)
  const pts: Pt2[] = []
  for (const p of raw) {
    const q = pts[pts.length - 1]
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-4) pts.push([p[0], p[1]])
  }
  if (pts.length < 2) return null
  const stations = [0]
  for (let i = 1; i < pts.length; i++) stations.push(stations[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]))
  const lengthFt = stations[stations.length - 1]
  if (lengthFt < 0.25) return null
  const T = el.typeParams, P = el.params
  const width = Math.max(1, numOr(P.widthFt, numOr(T.widthFt, 4)))
  const side = P.crossSlopeSide === 'left' || P.crossSlopeSide === 'crown' ? P.crossSlopeSide : 'right'
  const edge = T.edgeType === 'thickened' || T.edgeType === 'curb' ? T.edgeType : 'flush'
  const kind = T.pavingKind === 'street' ? 'street' : 'run'
  const breaks = (Array.isArray(P.gradeBreaks) ? P.gradeBreaks : [])
    .map((b) => b as { stationFt?: unknown; zFt?: unknown })
    .filter((b) => typeof b.stationFt === 'number' && typeof b.zFt === 'number' && b.stationFt > 0.5 && b.stationFt < lengthFt - 0.5)
    .map((b) => ({ stationFt: b.stationFt as number, zFt: b.zFt as number }))
    .sort((a, b) => a.stationFt - b.stationFt)
  const street = kind === 'street' ? streetSection(el) : null
  return {
    kind, street,
    pts, stations, lengthFt,
    // a street's footprint: lane edge to back of walk (for consumers that want a corridor)
    widthFt: street ? street.uMax - street.uMin : width,
    thicknessFt: Math.max(1 * IN, numOr(T.thicknessFt, 4 * IN)),
    jointSpacingFt: Math.max(0, numOr(T.jointSpacingFt, 5)),
    crossSlopePct: Math.max(0, numOr(P.crossSlopePct, numOr(T.crossSlopePct, 1.5))),
    crossSlopeSide: side,
    maxSlopePct: numOr(T.maxSlopePct, 5),
    edge,
    thickenedWidthFt: Math.min(width / 2, numOr(T.thickenedWidthFt, 12 * IN)),
    thickenedDepthFt: numOr(T.thickenedDepthFt, 4 * IN),
    curbHeightFt: numOr(T.curbHeightFt, 6 * IN),
    curbWidthFt: Math.min(width / 3, numOr(T.curbWidthFt, 6 * IN)),
    materialId: typeof T.surfaceMaterial === 'string' ? T.surfaceMaterial : '',
    startZ: endZ(el, 'start'),
    endZ: endZ(el, 'end'),
    breaks,
  }
}

/** the profile's control points (station, z): ends + breaks, pins applied */
export function pavingProfile(spec: PavingSpec, pins?: PavingPins): { s: number; z: number }[] {
  const z0 = typeof pins?.startZ === 'number' ? pins.startZ : spec.startZ
  const z1 = typeof pins?.endZ === 'number' ? pins.endZ : spec.endZ
  return [{ s: 0, z: z0 }, ...spec.breaks.map((b) => ({ s: b.stationFt, z: b.zFt })), { s: spec.lengthFt, z: z1 }]
}

/** profile z at station s (piecewise linear — an even fall between control points) */
export function profileZAt(profile: { s: number; z: number }[], s: number): number {
  if (s <= profile[0].s) return profile[0].z
  for (let i = 1; i < profile.length; i++) {
    const a = profile[i - 1], b = profile[i]
    if (s <= b.s) return b.s - a.s < 1e-9 ? b.z : a.z + ((b.z - a.z) * (s - a.s)) / (b.s - a.s)
  }
  return profile[profile.length - 1].z
}

/** cross-slope z offset at signed offset t (left of travel = +); the low edge is −w/2·pct */
export function crossZAt(spec: PavingSpec, t: number): number {
  const g = spec.crossSlopePct / 100
  const h = spec.widthFt / 2
  const tc = Math.max(-h, Math.min(h, t))
  if (spec.crossSlopeSide === 'crown') return -g * Math.abs(tc)
  // the HIGH side sits at 0 — the profile is the paving's high-edge elevation, so a pin
  // to a garage slab sets the slab-side edge, the way a finisher sets the forms
  return spec.crossSlopeSide === 'right' ? -g * (h - tc) : -g * (h + tc)
}

/**
 * Transverse joint stations, 0 and L included. Every profile control point (a pin end, a
 * grade break) is a joint; each interval between them is divided evenly at ≤ spacing.
 * `custom` (hand-drawn joints) replaces the automatic division but keeps the breaks.
 */
export function pavingJointStations(spec: PavingSpec, custom?: number[] | null): number[] {
  const fixed = [0, ...spec.breaks.map((b) => b.stationFt), spec.lengthFt]
  const out: number[] = []
  if (custom && custom.length) {
    out.push(...fixed, ...custom.filter((s) => s > 0.25 && s < spec.lengthFt - 0.25))
  } else {
    for (let i = 0; i + 1 < fixed.length; i++) {
      const a = fixed[i], b = fixed[i + 1]
      const n = spec.jointSpacingFt > 0 ? Math.max(1, Math.ceil((b - a) / spec.jointSpacingFt - 1e-6)) : 1
      for (let k = 0; k < n; k++) out.push(a + ((b - a) * k) / n)
    }
    out.push(spec.lengthFt)
  }
  const sorted = [...new Set(out.map((s) => Math.min(spec.lengthFt, Math.round(s * 1e6) / 1e6)))].sort((x, y) => x - y)
  // merge near-duplicates (a custom joint on top of a break)
  return sorted.filter((s, i) => i === 0 || s - sorted[i - 1] > 0.1)
}

/** the element's hand-drawn joint stations, or null (automatic) */
export function pavingCustomJoints(el: ResolvedElement): number[] | null {
  const j = el.params.joints as unknown
  if (!Array.isArray(j)) return null
  const out = j.filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
  return out.length ? out : null
}

/** longitudinal strip boundaries across the width, as signed offsets left(+) → right(−) */
export function pavingStripOffsets(spec: PavingSpec): number[] {
  const h = spec.widthFt / 2
  const n = spec.jointSpacingFt > 0 ? Math.max(1, Math.ceil(spec.widthFt / spec.jointSpacingFt - 1e-6)) : 1
  const out: number[] = []
  for (let k = 0; k <= n; k++) out.push(h - (spec.widthFt * k) / n)
  // a crowned run always breaks on its crown
  if (spec.crossSlopeSide === 'crown' && !out.some((t) => Math.abs(t) < 1e-6)) out.push(0)
  return [...new Set(out)].sort((a, b) => b - a)
}

/** running slope of each profile segment (%), for the palette and later warnings */
export function pavingRunningSlopes(spec: PavingSpec, pins?: PavingPins): { s0: number; s1: number; pct: number }[] {
  const prof = pavingProfile(spec, pins)
  const out: { s0: number; s1: number; pct: number }[] = []
  for (let i = 1; i < prof.length; i++) {
    const ds = prof[i].s - prof[i - 1].s
    if (ds > 1e-9) out.push({ s0: prof[i - 1].s, s1: prof[i].s, pct: ((prof[i].z - prof[i - 1].z) / ds) * 100 })
  }
  return out
}

/** the centerline frame at station s: point, unit tangent, left normal */
export function pavingFrameAt(spec: PavingSpec, s: number): { p: Pt2; tan: Pt2; nrm: Pt2 } {
  const { pts, stations } = spec
  let i = 1
  while (i < stations.length - 1 && stations[i] < s) i++
  const a = pts[i - 1], b = pts[i]
  const len = stations[i] - stations[i - 1] || 1
  const u = Math.max(0, Math.min(1, (s - stations[i - 1]) / len))
  const tx = (b[0] - a[0]) / len, ty = (b[1] - a[1]) / len
  return { p: [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u], tan: [tx, ty], nrm: [-ty, tx] }
}

/**
 * STREETS (2026-10-20, docs/bim-plan/35-streets-and-approaches.md): a street is drawn along
 * OUR curb's FLOWLINE (where the gutter meets the curb — the line the survey shoots), for the
 * frontage only, with lines and arcs like any paving run. Its profile is the flowline
 * elevation (the run's end heights + its CURB SHOTS, `gradeBreaks`); everything else is this
 * cross section, in `u` = feet from the flowline TOWARD the property (negative = out into the
 * street) and `dz` = feet above the flowline:
 *
 *   street paving (rising at the street cross slope toward the crown) · gutter (rising from
 *   the flowline to its lip) · curb (vertical: a plumb face at the flowline; rolled: a sloped
 *   face over the roll width; or none) · parkway (the terrain) · sidewalk — the last two
 *   rising away from the street.
 *
 * Every size is a TYPE value seeded as a typical default the firm edits — cities each have
 * their own curb and gutter standard, so none is presented as a city's.
 */
export interface StreetPiece {
  key: 'lane' | 'curb' | 'walk'
  /** closed (u, dz) polygon */
  poly: Pt2[]
  materialId: string
}
export interface StreetSection {
  /** the property side as the sign of the ribbon offset t (t = side * u; left of travel = +) */
  side: 1 | -1
  curbType: 'vertical' | 'roll' | 'none'
  gutterWidthFt: number
  curbHeightFt: number
  /** back of curb (u) */
  backOfCurbU: number
  /** sidewalk front and back (u); equal when there is no walk */
  walkU0: number
  walkU1: number
  /** the outer edge of the street paving shown (u, negative) and the back of the section */
  uMin: number
  uMax: number
  pieces: StreetPiece[]
  /** plan lines along the street */
  lines: { u: number; key: 'laneEdge' | 'lip' | 'flowline' | 'rollTop' | 'backOfCurb' | 'walkFront' | 'backOfWalk' }[]
  /** the finished surface above the flowline at u */
  topAt(u: number): number
  /** the earth under it (the underside of what is built there; the surface itself in the parkway) */
  underAt(u: number): number
  walkJointSpacingFt: number
  /** where a driveway meets this street (a vertical curb only — a rolled curb or no curb is
   *  ridden over, never cut): the curb drops to `lipFt` over the drive, ramps back up over
   *  `wingFt` each side, and an apron climbs from the lip to the sidewalk over `depthFt` */
  approach: { lipFt: number; wingFt: number; depthFt: number; thicknessFt: number } | null
}

export function streetSection(el: ResolvedElement): StreetSection {
  const T = el.typeParams, P = el.params
  const ct = T.curbType === 'roll' || T.curbType === 'none' ? T.curbType : 'vertical'
  const gW = T.gutter === false ? 0 : Math.max(0, numOr(T.gutterWidthFt, 2))
  const gL = (gW * Math.max(0, numOr(T.gutterSlopePct, 6.25))) / 100
  const ch = ct === 'none' ? 0 : Math.max(0, numOr(T.streetCurbHeightFt, 6 * IN))
  const cw = ct === 'none' ? 0 : Math.max(2 * IN, numOr(T.streetCurbWidthFt, 6 * IN))
  const rw = ct === 'roll' ? Math.max(3 * IN, numOr(T.rollWidthFt, 1)) : 0
  const bc = ct === 'none' ? 0 : rw + cw
  const cd = Math.max(3 * IN, numOr(T.curbDepthFt, 8 * IN))
  const lW = Math.max(0, numOr(T.laneWidthFt, 10))
  const ls = Math.max(0, numOr(T.laneSlopePct, 2)) / 100
  const lt = Math.max(1 * IN, numOr(T.laneThicknessFt, 4 * IN))
  const pk = Math.max(0, numOr(T.parkwayWidthFt, 0))
  const wW = Math.max(0, numOr(T.walkWidthFt, 5))
  const wt = Math.max(1 * IN, numOr(T.walkThicknessFt, 4 * IN))
  const ws = Math.max(0, numOr(T.walkSlopePct, 2)) / 100
  const w0 = bc + pk, w1 = w0 + wW
  const laneTop = (u: number) => gL + (-gW - u) * ls
  // a rolled curb's face: a gentle S from the flowline up to its top (smoothstep)
  const rollTop = (u: number) => {
    const x = Math.max(0, Math.min(1, u / rw))
    return ch * x * x * (3 - 2 * x)
  }
  const curbTop = (u: number) => (ct === 'roll' && u < rw ? rollTop(u) : ch)
  const walkTop = (u: number) => ch + (u - bc) * ws
  const concrete = typeof T.surfaceMaterial === 'string' ? T.surfaceMaterial : ''
  const asphalt = typeof T.laneMaterial === 'string' && T.laneMaterial ? T.laneMaterial : concrete
  const pieces: StreetPiece[] = []
  if (lW > 0) {
    const u0 = -gW - lW
    pieces.push({ key: 'lane', materialId: asphalt, poly: [[u0, laneTop(u0)], [-gW, gL], [-gW, gL - lt], [u0, laneTop(u0) - lt]] })
  }
  if (gW > 0 || ct !== 'none') {
    const top: Pt2[] = []
    if (gW > 0) top.push([-gW, gL], [0, 0])
    if (ct === 'vertical') top.push([0, ch], [bc, ch])
    else if (ct === 'roll') {
      if (!top.length) top.push([0, 0])
      for (let k = 1; k <= 6; k++) top.push([(rw * k) / 6, rollTop((rw * k) / 6)])
      top.push([bc, ch])
    }
    const u0 = top[0][0], u1 = top[top.length - 1][0]
    pieces.push({ key: 'curb', materialId: concrete, poly: [...top, [u1, -cd], [u0, -cd]] })
  }
  if (wW > 0) pieces.push({ key: 'walk', materialId: concrete, poly: [[w0, walkTop(w0)], [w1, walkTop(w1)], [w1, walkTop(w1) - wt], [w0, walkTop(w0) - wt]] })
  const lines: StreetSection['lines'] = []
  if (lW > 0) lines.push({ u: -gW - lW, key: 'laneEdge' })
  if (gW > 0) lines.push({ u: -gW, key: 'lip' })
  lines.push({ u: 0, key: 'flowline' })
  if (ct === 'roll') lines.push({ u: rw, key: 'rollTop' })
  if (ct !== 'none') lines.push({ u: bc, key: 'backOfCurb' })
  if (wW > 0) {
    if (w0 > bc + 1e-6) lines.push({ u: w0, key: 'walkFront' })
    lines.push({ u: w1, key: 'backOfWalk' })
  }
  const topAt = (u: number): number => {
    if (u <= -gW) return laneTop(Math.max(u, -gW - lW))
    if (u < 0) return gW > 0 ? gL * (-u / gW) : 0
    if (u < bc) return curbTop(u)
    return walkTop(Math.min(u, Math.max(w1, bc)))
  }
  const underAt = (u: number): number => {
    if (u <= -gW && lW > 0) return topAt(u) - lt
    if (u < bc || (u <= 0 && gW > 0)) return -cd
    if (wW > 0 && u >= w0 - 1e-9 && u <= w1 + 1e-9) return walkTop(u) - wt
    return topAt(u)
  }
  return {
    side: P.propertySide === 'right' ? -1 : 1,
    curbType: ct, gutterWidthFt: gW, curbHeightFt: ch, backOfCurbU: bc, walkU0: w0, walkU1: w1,
    uMin: lW > 0 ? -gW - lW : -gW, uMax: Math.max(bc, w1),
    pieces, lines, topAt, underAt, walkJointSpacingFt: Math.max(0, numOr(T.jointSpacingFt, 5)),
    approach: ct === 'vertical'
      ? {
          lipFt: Math.max(0, Math.min(ch, numOr(T.approachLipFt, 1 * IN))),
          wingFt: Math.max(0, numOr(T.approachWingFt, 3)),
          depthFt: Math.max(bc + 0.25, numOr(T.approachDepthFt, 3)),
          thicknessFt: Math.max(3 * IN, numOr(T.approachThicknessFt, 6 * IN)),
        }
      : null,
  }
}

/** which side of a drawn flowline a point (the house) is on, as the propertySide value */
export function streetSideToward(pts: Pt2[], target: Pt2): 'left' | 'right' {
  let best = Infinity
  let side: 'left' | 'right' = 'left'
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const l2 = dx * dx + dy * dy
    if (l2 < 1e-12) continue
    const u = Math.max(0, Math.min(1, ((target[0] - a[0]) * dx + (target[1] - a[1]) * dy) / l2))
    const d = Math.hypot(target[0] - a[0] - dx * u, target[1] - a[1] - dy * u)
    if (d < best) {
      best = d
      side = dx * (target[1] - a[1]) - dy * (target[0] - a[0]) >= 0 ? 'left' : 'right'
    }
  }
  return side
}
